/*
 * ConfirmModal — reusable in-app confirmation dialog.
 *
 * Two modes:
 *   - Simple confirm:       caller passes `title` + `body`. User clicks
 *                           Confirm or Cancel. Esc cancels. Backdrop
 *                           click cancels.
 *   - Type-to-confirm:      caller passes `requireType="Delete Event"`.
 *                           The Confirm button stays disabled until the
 *                           user types that string exactly. Used for
 *                           destructive actions where a misclick is
 *                           expensive.
 *
 * Why not the browser's confirm()?
 *   - Native confirm() blocks the render thread and looks like an OS
 *     alert (white box on a desktop browser). It also lacks the typed-
 *     confirm pattern we want for delete.
 *   - This modal participates in the app's design tokens, supports
 *     keyboard input, and renders consistently across browsers.
 *
 * Accessibility:
 *   - The modal is dismissible with Escape and backdrop click.
 *   - The first input or button receives focus on open.
 *   - Backdrop has aria-hidden; the dialog has role="dialog" and an
 *     accessible name from the title.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If set, the user must type this string to enable the Confirm button. */
  requireType?: string;
  /** Visual treatment of the confirm button. Default 'primary'. */
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  /** Disable the modal while an async confirm is in flight. */
  busy?: boolean;
}

export function ConfirmModal({
  open, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  requireType, tone = 'primary', onConfirm, onCancel, busy,
}: ConfirmModalProps) {
  const [typedValue, setTypedValue] = useState('');
  const firstFocusRef = useRef<HTMLInputElement | HTMLButtonElement | null>(null);

  // Reset the typed input every time the modal opens.
  useEffect(() => {
    if (open) {
      setTypedValue('');
      // Defer focus to next tick so the element is in the DOM.
      const t = setTimeout(() => firstFocusRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Escape to cancel.
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  // Trimmed, case-insensitive match: the friction that makes type-to-
  // confirm deliberate is the typing itself, not the capitalization.
  // Matters for name confirms ("andrew" should pass for "Andrew") and
  // is harmless for DELETE confirms.
  const canConfirm = !busy &&
    (requireType ? typedValue.trim().toLowerCase() === requireType.trim().toLowerCase() : true);

  return (
    <div
      role="presentation"
      onClick={() => { if (!busy) onCancel(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.66)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        // Top-anchored, not centered: when the iOS keyboard opens for the
        // type-to-confirm input, a centered dialog's middle sits behind
        // the keyboard and the title scrolls off the top with no way to
        // reach it. Anchoring near the top keeps the title + input above
        // the keyboard. The overlay scrolls if the dialog is tall.
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: '6vh 20px 20px',
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 22,
          maxWidth: 440,
          width: '100%',
          // flex-shrink:0 so the overlay (now scrollable + top-anchored)
          // doesn't squash the dialog; it scrolls as a whole instead.
          flexShrink: 0,
          boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2
          id="confirm-modal-title"
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 900,
            letterSpacing: '-0.01em',
            color: 'var(--text)',
          }}
        >
          {title}
        </h2>

        {body && (
          <div style={{ color: 'var(--text-sec)', fontSize: 14, lineHeight: 1.5 }}>
            {body}
          </div>
        )}

        {requireType && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: 'var(--text-sec)' }}>
              TYPE "{requireType}" TO CONFIRM
            </span>
            <input
              ref={firstFocusRef as React.RefObject<HTMLInputElement>}
              type="text"
              className="t-input"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirm) onConfirm();
              }}
              // On iOS the keyboard opening shrinks the visual viewport;
              // pull the input back into view after the animation settles.
              onFocus={(e) => {
                const el = e.currentTarget;
                setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
              }}
              placeholder={requireType}
              disabled={busy}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </label>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="t-btn t-btn-ghost"
            onClick={onCancel}
            disabled={busy}
            ref={requireType ? undefined : (firstFocusRef as React.RefObject<HTMLButtonElement>)}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 't-btn t-btn-danger' : 't-btn t-btn-primary'}
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{ minWidth: 100 }}
          >
            {busy ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
